import type { TagProps } from '@coursera/cds-core';

import type { StyledPillTag } from 'bundles/product-card/components/LearningProductCardColorCodedPillTags';
import { getMarketingStyledPillTag } from 'bundles/product-card/components/utils/getMarketingTagStyle';
import {
  getMarketingStatusTagForSlug,
  isLegacyBestsellerSlug,
  isNewMerchPillCategory,
  makesNewTagRedundant,
} from 'bundles/product-card/components/utils/marketingTagUtils';
import { useShouldShowNewMerchPills } from 'bundles/product-card/hooks/useShouldShowNewMerchPills';
import { BADGE_NAMES } from 'bundles/product-card/utils/getProductCardStatusTags';

type ProductCardPillModelOptions = {
  slug?: string;
  statusTagNames?: string[];
  canShowMarketingPill: boolean;
  hideAllStatusTags?: boolean;
  fallbackStatusTag?: string | false;
  topMatchLabel?: string;
  maxPills?: number;
  size?: TagProps['size'];
};

type ProductCardPillModel = {
  pillTagNames?: string[];
  styledPillTags: StyledPillTag[];
};

const matchesTagName = (tagName: string, expected: string) => tagName.toLowerCase() === expected.toLowerCase();

const useProductCardPillModel = ({
  slug,
  statusTagNames = [],
  canShowMarketingPill,
  hideAllStatusTags = false,
  fallbackStatusTag,
  topMatchLabel,
  maxPills,
  size,
}: ProductCardPillModelOptions): ProductCardPillModel => {
  const sponsoredTag = statusTagNames.find((tagName) => matchesTagName(tagName, BADGE_NAMES.SPONSORED));
  const topMatchPillCount = topMatchLabel && !sponsoredTag ? 1 : 0;
  const hasMarketingPillSlot = maxPills === undefined || maxPills > topMatchPillCount;
  const canRenderMarketingPill = canShowMarketingPill && !hideAllStatusTags && !sponsoredTag && hasMarketingPillSlot;
  const slugMarketingTag =
    slug && (canRenderMarketingPill || sponsoredTag)
      ? getMarketingStatusTagForSlug(slug, { includeNewMerchPills: true })
      : undefined;
  const isNewMerchPillCard = isNewMerchPillCategory(slugMarketingTag?.category);
  const shouldShowNewMerchPills = useShouldShowNewMerchPills(isNewMerchPillCard && canRenderMarketingPill);
  const showsLegacyBestseller =
    slugMarketingTag?.category === 'bestseller' &&
    Boolean(slug) &&
    isLegacyBestsellerSlug(slug ?? '') &&
    !shouldShowNewMerchPills;
  const marketingTag =
    isNewMerchPillCard && !shouldShowNewMerchPills && !showsLegacyBestseller ? undefined : slugMarketingTag;

  if (hideAllStatusTags) {
    return { pillTagNames: undefined, styledPillTags: [] };
  }

  let pillTagNames = [...statusTagNames];

  if (marketingTag) {
    const remainingTagNames = makesNewTagRedundant(marketingTag.category)
      ? pillTagNames.filter((tagName) => !matchesTagName(tagName, BADGE_NAMES.NEW))
      : pillTagNames;
    pillTagNames = sponsoredTag ? [sponsoredTag] : [marketingTag.tag, ...remainingTagNames];
  } else if (fallbackStatusTag && !pillTagNames.some((tagName) => matchesTagName(tagName, fallbackStatusTag))) {
    pillTagNames.push(fallbackStatusTag);
  }

  if (topMatchLabel && !sponsoredTag) {
    pillTagNames = [topMatchLabel, ...pillTagNames];
  }

  const effectiveMaxPills = sponsoredTag && topMatchLabel && maxPills ? Math.max(1, maxPills - 1) : maxPills;

  if (effectiveMaxPills !== undefined) {
    const sponsoredTagAtCap = pillTagNames.find((tagName) => matchesTagName(tagName, BADGE_NAMES.SPONSORED));
    pillTagNames = pillTagNames.slice(0, effectiveMaxPills);

    if (sponsoredTagAtCap && !pillTagNames.includes(sponsoredTagAtCap)) {
      pillTagNames = effectiveMaxPills === 1 ? [sponsoredTagAtCap] : [...pillTagNames.slice(0, -1), sponsoredTagAtCap];
    }
  }

  const styledPillTag =
    marketingTag && !showsLegacyBestseller && pillTagNames.some((tagName) => tagName === marketingTag.tag)
      ? getMarketingStyledPillTag(marketingTag, size)
      : undefined;

  return {
    pillTagNames,
    styledPillTags: styledPillTag ? [styledPillTag] : [],
  };
};

export default useProductCardPillModel;
